import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import { TIDS } from "@/lib/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUDGETS = [
  "Under $1,000 / mo",
  "$1,000 – $3,000 / mo",
  "$3,000 – $7,500 / mo",
  "$7,500 – $15,000 / mo",
  "$15,000+ / mo",
];

const TIME_SLOTS = [
  "9:00 AM",
  "10:00 AM",
  "11:00 AM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
  "4:00 PM",
];

function formatDate(d) {
  if (!d) return "";
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default function BookingForm() {
  const [form, setForm] = useState({
    full_name: "",
    practice_name: "",
    email: "",
    phone: "",
    monthly_ads_budget: "",
    preferred_date: null,
    preferred_time: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        full_name: form.full_name,
        practice_name: form.practice_name || null,
        email: form.email,
        phone: form.phone,
        monthly_ads_budget: form.monthly_ads_budget || null,
        preferred_date: form.preferred_date
          ? form.preferred_date.toISOString().slice(0, 10)
          : null,
        preferred_time: form.preferred_time || null,
        message: form.message || null,
        source: "strategy_call",
      });
      setDone(true);
      if (window.posthog) window.posthog.capture("lead_submitted", { source: "strategy_call" });
      toast.success("You're in. We'll reach out within 24 hours.");
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid={TIDS.formSection}
      className="py-24 md:py-32 relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left column */}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <div className="font-mono-accent text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-4">
              / 05 · Book your free call
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tighter font-medium text-white leading-[1.05]">
              A 30-minute call. A 12-month plan. Zero pressure.
            </h2>
            <p className="mt-6 text-lg text-white/60 leading-relaxed max-w-lg">
              Tell us about your practice. We&apos;ll show you exactly where your
              new patients are hiding, what it&apos;ll cost to reach them, and what
              your first 90 days with us would look like.
            </p>

            <ul className="mt-10 space-y-4">
              {[
                "Custom growth audit, yours to keep",
                "Live competitor & keyword breakdown",
                "Realistic 90-day new-patient forecast",
                "No pitch. No pressure. No fluff.",
              ].map((l) => (
                <li key={l} className="flex items-start gap-3 text-white/80">
                  <CheckCircle2 className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                  <span>{l}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex items-center gap-3 text-xs text-white/50">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span>Your details are private. We never spam or share data.</span>
            </div>
          </div>

          {/* Right column - form */}
          <div className="lg:col-span-7">
            <div className="relative rounded-3xl border border-white/10 bg-white/[0.035] backdrop-blur-2xl p-8 md:p-10 shadow-[0_30px_80px_rgba(0,0,0,0.4)]">
              <div className="absolute -top-3 left-8 bg-amber-400 text-[#070B14] text-[10px] font-mono-accent uppercase tracking-[0.2em] px-3 py-1 rounded-full">
                Free Strategy Call
              </div>

              {done ? (
                <div className="py-16 text-center">
                  <div className="w-16 h-16 rounded-full bg-amber-400/20 border border-amber-400/40 mx-auto flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-amber-400" />
                  </div>
                  <h3 className="mt-6 font-display text-3xl text-white">
                    You&apos;re on the calendar.
                  </h3>
                  <p className="mt-3 text-white/60 max-w-md mx-auto">
                    Our team will reach out within 24 hours to confirm your
                    slot and send over your custom audit prep.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Full name" required>
                      <Input
                        data-testid={TIDS.inputName}
                        value={form.full_name}
                        onChange={(e) => update("full_name", e.target.value)}
                        placeholder="Dr. Jane Smith"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                        required
                      />
                    </Field>
                    <Field label="Practice name">
                      <Input
                        data-testid={TIDS.inputPractice}
                        value={form.practice_name}
                        onChange={(e) => update("practice_name", e.target.value)}
                        placeholder="Bright Smile Dental"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Email" required>
                      <Input
                        data-testid={TIDS.inputEmail}
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="jane@practice.com"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                        required
                      />
                    </Field>
                    <Field label="Phone" required>
                      <Input
                        data-testid={TIDS.inputPhone}
                        type="tel"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="+1 (555) 123-4567"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                        required
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Monthly ads budget">
                      <Select
                        value={form.monthly_ads_budget}
                        onValueChange={(v) => update("monthly_ads_budget", v)}
                      >
                        <SelectTrigger
                          data-testid={TIDS.inputBudget}
                          className="bg-white/5 border-white/10 text-white h-12"
                        >
                          <SelectValue placeholder="Select a range" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f172a] border-white/10 text-white">
                          {BUDGETS.map((b) => (
                            <SelectItem key={b} value={b} className="focus:bg-white/10">
                              {b}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field label="Preferred call date">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            data-testid={TIDS.inputDate}
                            type="button"
                            className="w-full h-12 px-3 rounded-md bg-white/5 border border-white/10 text-left text-white flex items-center justify-between hover:border-white/25 transition-colors duration-200"
                          >
                            <span
                              className={
                                form.preferred_date
                                  ? "text-white"
                                  : "text-white/30"
                              }
                            >
                              {form.preferred_date
                                ? formatDate(form.preferred_date)
                                : "Pick a date"}
                            </span>
                            <CalendarIcon className="w-4 h-4 text-white/40" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto p-0 bg-[#0f172a] border-white/10"
                          align="start"
                        >
                          <Calendar
                            mode="single"
                            selected={form.preferred_date}
                            onSelect={(d) => update("preferred_date", d)}
                            disabled={(d) =>
                              d < new Date(new Date().setHours(0, 0, 0, 0))
                            }
                            initialFocus
                            className="text-white"
                          />
                        </PopoverContent>
                      </Popover>
                    </Field>
                  </div>

                  <Field label="Preferred call time">
                    <Select
                      value={form.preferred_time}
                      onValueChange={(v) => update("preferred_time", v)}
                    >
                      <SelectTrigger
                        data-testid={TIDS.inputTime}
                        className="bg-white/5 border-white/10 text-white h-12"
                      >
                        <SelectValue placeholder="Select a time slot" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0f172a] border-white/10 text-white">
                        {TIME_SLOTS.map((t) => (
                          <SelectItem key={t} value={t} className="focus:bg-white/10">
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Anything we should know?">
                    <Textarea
                      data-testid={TIDS.inputMessage}
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="Current biggest bottleneck, goals, geography, procedures you want to grow…"
                      rows={4}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none"
                    />
                  </Field>

                  <Button
                    data-testid={TIDS.submitBooking}
                    type="submit"
                    disabled={submitting}
                    className="group w-full h-13 py-4 rounded-full bg-amber-400 text-[#070B14] hover:bg-amber-300 font-medium text-base transition-colors duration-200 active:scale-[0.98] disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : "Book my free strategy call"}
                    {!submitting && (
                      <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
                    )}
                  </Button>
                  <p className="text-center text-xs text-white/40 pt-1">
                    By submitting, you agree to be contacted about your inquiry.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs font-mono-accent uppercase tracking-[0.18em] text-white/60 mb-2 block">
        {label} {required && <span className="text-amber-400">*</span>}
      </Label>
      {children}
    </div>
  );
}
